import { useState, useEffect } from 'react'
import { App, Typography, theme, Flex, Alert } from 'antd'
import { useNavigate, useLocation } from 'react-router-dom'
import { CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import { AuthService } from '../services/auth/authService'
import { HttpClient } from '../services/api/client'
import { EmailConfirmation } from '../components/Auth'
import { PartnerSignUpFlow, type PartnerSignUpFlowData } from '../components/Auth/PartnerSignUpFlow'
import { useAuthContext } from '../context/auth'
import { supabase } from '../utils/supabase'
import {
  PageContainer,
  CardContainer,
  CenteredTitle,
  AlertDescription,
  LogoLink,
  CompactLinkButton,
} from './styles/LoginPageStyles'
import { SPACING } from '../styles/theme'

const { Text } = Typography
const { useToken } = theme

const WiderCardContainer = styled(CardContainer)`
  max-width: 480px;
`

const PageSubtitle = styled(Text)`
  display: block;
  text-align: center;
  margin-bottom: ${SPACING.lg};
`

// Helper to parse and map Supabase error codes to user-friendly messages
function parseEmailConfirmationError(
  hash: string
): { message: string; description: string } | null {
  const errorMatch = hash.match(/error=([^&]+)/)
  const errorCodeMatch = hash.match(/error_code=([^&]+)/)
  const errorDescriptionMatch = hash.match(/error_description=([^&]+)/)

  if (!errorMatch && !errorCodeMatch) {
    return null
  }

  const errorCode = errorCodeMatch ? decodeURIComponent(errorCodeMatch[1]) : null
  const errorDescription = errorDescriptionMatch
    ? decodeURIComponent(errorDescriptionMatch[1].replace(/\+/g, ' '))
    : null

  const errorMessages: Record<string, { message: string; description: string }> = {
    otp_expired: {
      message: 'Confirmation Link Expired',
      description:
        'This email confirmation link has expired. Please request a new confirmation email or try signing up again.',
    },
    email_not_confirmed: {
      message: 'Email Not Confirmed',
      description:
        'Your email address has not been confirmed yet. Please check your inbox for a confirmation email.',
    },
    invalid_token: {
      message: 'Invalid Link',
      description: 'This confirmation link is invalid. Please request a new confirmation email.',
    },
  }

  if (errorCode && errorCode in errorMessages) {
    return errorMessages[errorCode]
  }

  return {
    message: 'Email confirmation failed',
    description: errorDescription ?? 'The confirmation link is invalid or has expired.',
  }
}

// Check if URL contains email confirmation tokens
function hasEmailConfirmationTokens(hash: string, searchParams: URLSearchParams): boolean {
  return (
    (hash.includes('access_token') && hash.includes('type=email')) ||
    searchParams.get('type') === 'email'
  )
}

// SessionStorage keys for tracking confirmation state
const STORAGE_KEYS = {
  CONFIRMATION_PENDING: 'partner_email_confirmation_pending',
  CONFIRMATION_SUCCESS: 'partner_email_confirmation_success',
} as const

export function PartnerSignUpPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuthContext()
  const [loading, setLoading] = useState(false)
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false)
  const [emailConfirmed, setEmailConfirmed] = useState(false)
  const [emailError, setEmailError] = useState<{ message: string; description: string } | null>(
    null
  )
  const { token } = useToken()
  const { notification } = App.useApp()

  // Clear auth redirect flag when landing on this page
  useEffect(() => {
    sessionStorage.removeItem('authRedirecting')
  }, [])

  // Handle URL tokens for email confirmation
  useEffect(() => {
    const hash = location.hash
    const searchParams = new URLSearchParams(location.search)

    // Handle error cases
    const parsedError = parseEmailConfirmationError(hash)
    if (parsedError) {
      setEmailError(parsedError)
      window.history.replaceState({}, '', '/become-partner')
      return undefined
    }

    // Handle email confirmation tokens
    if (hasEmailConfirmationTokens(hash, searchParams)) {
      sessionStorage.setItem(STORAGE_KEYS.CONFIRMATION_PENDING, 'true')

      // Listen for Supabase to process the confirmation
      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (['SIGNED_IN', 'TOKEN_REFRESHED', 'USER_UPDATED'].includes(event)) {
          if (session?.user.email_confirmed_at) {
            // Show success message
            setEmailConfirmed(true)
            setEmailError(null)
            sessionStorage.setItem(STORAGE_KEYS.CONFIRMATION_SUCCESS, 'true')
            sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_PENDING)
            window.history.replaceState({}, '', '/become-partner')

            // Sign user out so they need to log in manually
            await supabase.auth.signOut()

            subscription.unsubscribe()
          }
        }
      })

      return () => subscription.unsubscribe()
    }

    // Handle case where confirmation was already processed
    const wasSuccessful = sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_SUCCESS) === 'true'
    if (wasSuccessful) {
      void supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (session?.user.email_confirmed_at) {
          setEmailConfirmed(true)
          await supabase.auth.signOut()
          sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
        } else {
          sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
        }
      })
    }
    return undefined
  }, [location.hash, location.search])

  // Sync with auth context when user becomes confirmed
  useEffect(() => {
    if (!user?.email_confirmed_at || emailConfirmed || emailError) {
      return
    }

    const hasConfirmationFlag =
      sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_PENDING) === 'true' ||
      sessionStorage.getItem(STORAGE_KEYS.CONFIRMATION_SUCCESS) === 'true'

    if (hasConfirmationFlag) {
      setEmailConfirmed(true)
      void supabase.auth.signOut()
      sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_PENDING)
      sessionStorage.removeItem(STORAGE_KEYS.CONFIRMATION_SUCCESS)
    }
  }, [user, emailConfirmed, emailError])

  const handlePartnerSignUp = async (values: PartnerSignUpFlowData) => {
    setLoading(true)
    try {
      const { error } = await AuthService.signUpAsPartner(
        values.email,
        values.password,
        values.fullName,
        values.partnerTermsAccepted,
        values.newsletterOptIn,
        {
          surveyPromotionChannels: values.surveyPromotionChannels,
          surveyPromotionChannelsOther: values.surveyPromotionChannelsOther,
          surveyAudienceSize: values.surveyAudienceSize,
          surveyNiche: values.surveyNiche,
          surveyNicheOther: values.surveyNicheOther,
          surveyExperience: values.surveyExperience,
        }
      )
      if (error) {
        notification.error({
          title: 'Registration Failed',
          description: error.message,
          placement: 'top',
        })
      } else {
        // Notify admin of new partner application (fire and forget)
        void HttpClient.post('/public/partner/notify-application', {
          name: values.fullName,
          email: values.email,
          promotionChannels: values.surveyPromotionChannels,
          promotionChannelsOther: values.surveyPromotionChannelsOther || null,
          audienceSize: values.surveyAudienceSize || null,
          niche: values.surveyNiche || null,
          nicheOther: values.surveyNicheOther || null,
          experience: values.surveyExperience || null,
        })
        setShowEmailConfirmation(true)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleBackToSignUp = () => {
    setShowEmailConfirmation(false)
    setEmailError(null)
    setEmailConfirmed(false)
  }

  return (
    <PageContainer
      align="center"
      justify="center"
      $colorPrimary={token.colorPrimary}
      $colorBgContainer={token.colorBgContainer}
    >
      <LogoLink href="https://bandos.ai" $color={token.colorText}>
        Bandos
      </LogoLink>
      <WiderCardContainer
        $colorBgContainer={token.colorBgContainer}
        $borderRadius={token.borderRadius}
        $boxShadowTertiary={token.boxShadowTertiary}
      >
        {showEmailConfirmation ? (
          <EmailConfirmation onBackToLogin={handleBackToSignUp} />
        ) : (
          <Flex vertical gap="large">
            <div>
              <CenteredTitle level={2}>Apply to Partner Program</CenteredTitle>
              <PageSubtitle type="secondary">
                Submit your application to earn recurring commission as a Bandos partner
              </PageSubtitle>
            </div>

            {emailConfirmed && (
              <Alert
                message="Application Submitted!"
                description={
                  <AlertDescription>
                    Thank you for applying to the Bandos Partner Program! We&apos;ve received your
                    application and will review it shortly. You&apos;ll receive an email once your
                    application has been processed.
                  </AlertDescription>
                }
                type="success"
                showIcon
                icon={<CheckCircleOutlined />}
              />
            )}

            {emailError && (
              <Alert
                message={emailError.message}
                description={emailError.description}
                type="error"
                showIcon
                icon={<ExclamationCircleOutlined />}
                closable
                onClose={() => setEmailError(null)}
              />
            )}

            <PartnerSignUpFlow onFinish={handlePartnerSignUp} loading={loading} />

            <Flex justify="center" gap="small">
              <Text type="secondary">Already have an account?</Text>
              <CompactLinkButton type="link" onClick={() => void navigate('/login')}>
                Sign In
              </CompactLinkButton>
            </Flex>
          </Flex>
        )}
      </WiderCardContainer>
    </PageContainer>
  )
}
